import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAiAssistantSettings } from '@/hooks/use-ai-assistant-settings';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { v4 as uuidv4 } from 'uuid';
import { SessionType, TopicType, MessageType } from '@/types/assistant';
import { getWelcomeMessage } from '@/utils/welcomeMessageUtils';
import { getFeedbackForMessages } from '@/services/ai-feedback-service';

interface UseSessionManagementProps {
  onSessionChange?: (session: SessionType | null) => void;
  onMessagesChange?: (messages: MessageType[] | ((prevMessages: MessageType[]) => MessageType[])) => void;
}

interface UseSessionManagementReturn {
  sessions: SessionType[];
  currentSession: SessionType | null;
  fetchingSessions: boolean;
  createNewSession: (topic: TopicType) => Promise<string>;
  deleteSession: (sessionId: string) => Promise<boolean>;
  updateSessionTitle: (sessionId: string, title: string) => Promise<boolean>;
  selectSession: (sessionId: string) => Promise<void>;
  searchSessions: (query: string) => SessionType[];
  loadSession: (sessionId: string) => Promise<void>;
  setCurrentSession: (session: SessionType | null) => void;
  createNewConversation: (topic: TopicType) => Promise<string>;
}

// Default number of messages to load per page
const MESSAGES_PER_PAGE = 15;

export const useSessionManagement = ({
  onSessionChange,
  onMessagesChange
}: UseSessionManagementProps = {}): UseSessionManagementReturn => {
  const [sessions, setSessions] = useState<SessionType[]>([]);
  const [currentSession, setCurrentSession] = useState<SessionType | null>(null);
  const [fetchingSessions, setFetchingSessions] = useState<boolean>(true);
  
  const navigate = useNavigate();
  const { user } = useAuth();
  const { language } = useLanguage();
  const { lastSessionId, userMetadata, updateLastSession } = useAiAssistantSettings();

  // Load sessions on user authentication
  useEffect(() => {
    const loadSessions = async () => {
      if (!user?.id) {
        setFetchingSessions(false);
        return;
      }

      setFetchingSessions(true);
      try {
        const { data, error } = await supabase
          .from('assistant_sessions')
          .select('*')
          .eq('user_id', user.id)
          .eq('archived', false)
          .order('created_at', { ascending: false });

        if (error) {
          console.error('Error fetching sessions:', error);
          toast.error('Failed to load AI assistant sessions');
        } else {
          const formattedSessions: SessionType[] = data?.map(session => ({
            id: session.id,
            name_session: session.name_session,
            topic: session.topic,
            created_at: new Date(session.created_at),
            updated_at: new Date(session.updated_at),
            user_id: session.user_id,
          })) || [];
          
          setSessions(formattedSessions);
        }
      } finally {
        setFetchingSessions(false);
      }
    };

    loadSessions();
  }, [user?.id]);

  // Helper function to load feedback for messages
  const loadFeedbackForMessages = useCallback(async (messages: MessageType[], userId: string): Promise<MessageType[]> => {
    if (!messages.length || !userId) return messages;

    try {
      const messageIds = messages.map(msg => msg.id);
      const feedbackMap = await getFeedbackForMessages(messageIds, userId);

      return messages.map(msg => ({
        ...msg,
        feedback: feedbackMap[msg.id] || null
      }));
    } catch (error) {
      console.error('Error loading feedback for messages:', error);
      return messages; // Return original messages if feedback loading fails
    }
  }, []);

  // Function to load a specific session with proper message loading and feedback
  const loadSession = useCallback(async (sessionId: string) => {
    if (!user?.id) return;

    try {
      // Fetch session details
      const { data: sessionData, error: sessionError } = await supabase
        .from('assistant_sessions')
        .select('*')
        .eq('id', sessionId)
        .eq('user_id', user.id)
        .single();

      if (sessionError) {
        console.error('Error fetching session:', sessionError);
        
        if (sessionError.code === 'PGRST116') {
          toast.error('Session not found. It may have been deleted.');
          if (lastSessionId === sessionId) {
            await updateLastSession('');
          }
          return;
        }
        
        toast.error('Failed to load session');
        return;
      }

      if (!sessionData) {
        toast.error('Session not found');
        return;
      }

      const session: SessionType = {
        id: sessionData.id,
        name_session: sessionData.name_session,
        topic: sessionData.topic,
        created_at: new Date(sessionData.created_at),
        updated_at: new Date(sessionData.updated_at),
        user_id: sessionData.user_id,
      };
      
      setCurrentSession(session);
      onSessionChange?.(session);
      
      await updateLastSession(sessionId);

      // Fetch initial batch of messages (most recent first, then reverse for chronological order)
      const { data: messagesData, error: messagesError } = await supabase
        .from('assistant_messages')
        .select('*')
        .eq('session_id', sessionId)
        .order('created_at', { ascending: false })
        .limit(MESSAGES_PER_PAGE);

      if (messagesError) {
        console.error('Error fetching messages:', messagesError);
        toast.error('Failed to load messages');
        onMessagesChange?.([]);
        return;
      }

      let formattedMessages: MessageType[] = messagesData ? messagesData.map(msg => ({
        id: msg.id,
        content: msg.content,
        role: msg.role,
        timestamp: new Date(msg.created_at),
        session_id: msg.session_id,
        user_id: msg.user_id,
        suggestions: msg.suggestions || [],
        feedback: null, // Will be loaded separately
        is_streaming: false, // Messages from database are never streaming
      })).reverse() : []; // Reverse to get chronological order

      // Load feedback data for messages
      if (formattedMessages.length > 0 && user?.id) {
        formattedMessages = await loadFeedbackForMessages(formattedMessages, user.id);
      }

      onMessagesChange?.(formattedMessages);
      navigate(`/ai-assistant`);
    } catch (error) {
      console.error('Error in loadSession:', error);
      toast.error('Failed to load session');
    }
  }, [user?.id, navigate, updateLastSession, lastSessionId, onSessionChange, onMessagesChange, loadFeedbackForMessages]);

  const createNewSession = async (topic: TopicType): Promise<string> => {
    if (!user?.id) {
      toast.error('You must be logged in to create a session');
      return '';
    }

    const newSessionId = uuidv4();
    const defaultName = `New ${topic} Session`;

    try {
      // Create the session
      const { error } = await supabase
        .from('assistant_sessions')
        .insert([{
          id: newSessionId,
          name_session: defaultName,
          topic: topic,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          user_id: user.id,
        }]);

      if (error) {
        console.error('Error creating session:', error);
        toast.error('Failed to create new session');
        return '';
      }

      // Fetch the newly created session
      const { data: newSessionData, error: newSessionError } = await supabase
        .from('assistant_sessions')
        .select('*')
        .eq('id', newSessionId)
        .single();

      if (newSessionError) {
        console.error('Error fetching new session:', newSessionError);
        toast.error('Failed to load new session');
        return '';
      }

      const newSession: SessionType = {
        id: newSessionData.id,
        name_session: newSessionData.name_session,
        topic: newSessionData.topic,
        created_at: new Date(newSessionData.created_at),
        updated_at: new Date(newSessionData.updated_at),
        user_id: newSessionData.user_id,
      };

      // Update sessions list and set as current
      setSessions(prevSessions => [newSession, ...prevSessions]);
      setCurrentSession(newSession);
      onSessionChange?.(newSession);

      // Create and save welcome message with multilingual support
      const welcomeMessageId = uuidv4();
      const welcomeContent = getWelcomeMessage(topic, language);
      
      const welcomeMessage: MessageType = {
        id: welcomeMessageId,
        content: welcomeContent,
        role: 'assistant',
        timestamp: new Date(),
        session_id: newSessionId,
        user_id: user.id,
        suggestions: [],
        feedback: null,
        is_streaming: false, // Welcome messages are never streaming
      };

      // Save welcome message to database
      const { error: welcomeError } = await supabase
        .from('assistant_messages')
        .insert({
          id: welcomeMessageId,
          session_id: newSessionId,
          user_id: user.id,
          role: 'assistant',
          content: welcomeContent,
          created_at: welcomeMessage.timestamp.toISOString(),
          suggestions: welcomeMessage.suggestions || [],
        });

      if (welcomeError) {
        console.error('Error saving welcome message:', welcomeError);
        toast.error('Failed to create welcome message');
        // Continue without welcome message rather than failing entirely
        onMessagesChange?.([]);
      } else {
        // Successfully created welcome message
        onMessagesChange?.([welcomeMessage]);
      }

      await updateLastSession(newSessionId);
      navigate(`/ai-assistant`);
      return newSessionId;
    } catch (err) {
      console.error('Unexpected error creating session:', err);
      toast.error('Failed to create new session');
      return '';
    }
  };

  const deleteSession = async (sessionId: string): Promise<boolean> => {
    if (!user?.id) {
      toast.error('You must be logged in to delete a session');
      return false;
    }

    try {
      const isLastUsedSession = lastSessionId === sessionId;
      
      const { error } = await supabase
        .from('assistant_sessions')
        .update({ 
          archived: true,
          updated_at: new Date().toISOString() 
        })
        .eq('id', sessionId)
        .eq('user_id', user.id);

      if (error) {
        console.error('Error archiving session:', error);
        toast.error('Failed to delete session');
        return false;
      }

      if (isLastUsedSession) {
        try {
          await updateLastSession(null);
        } catch (err) {
          console.error('Error clearing lastSessionId:', err);
        }
      }

      setSessions(prevSessions => prevSessions.filter(session => session.id !== sessionId));
      if (currentSession?.id === sessionId) {
        setCurrentSession(null);
        onSessionChange?.(null);
        onMessagesChange?.([]);
      }
      navigate(`/ai-assistant`);
      return true;
    } catch (err) {
      console.error('Unexpected error archiving session:', err);
      toast.error('Failed to delete session');
      return false;
    }
  };

  const updateSessionTitle = async (sessionId: string, name: string): Promise<boolean> => {
    if (!user?.id) {
      toast.error('You must be logged in to update a session');
      return false;
    }

    try {
      const { error } = await supabase
        .from('assistant_sessions')
        .update({ name_session: name, updated_at: new Date().toISOString() })
        .eq('id', sessionId)
        .eq('user_id', user.id);

      if (error) {
        console.error('Error updating session title:', error);
        toast.error('Failed to update session name');
        return false;
      }

      setSessions(prevSessions =>
        prevSessions.map(session =>
          session.id === sessionId ? { ...session, name_session: name } : session
        )
      );
      
      if (currentSession?.id === sessionId) {
        const updatedSession = { ...currentSession, name_session: name };
        setCurrentSession(updatedSession);
        onSessionChange?.(updatedSession);
      }
      
      return true;
    } catch (err) {
      console.error('Unexpected error updating session name:', err);
      toast.error('Failed to update session name');
      return false;
    }
  };

  const selectSession = async (sessionId: string): Promise<void> => {
    if (!user?.id) return;
    await loadSession(sessionId);
  };

  const searchSessions = (query: string): SessionType[] => {
    if (!query.trim()) return sessions;
    
    const lowerCaseQuery = query.toLowerCase();
    return sessions.filter(session => 
      session.name_session.toLowerCase().includes(lowerCaseQuery)
    );
  };

  const createNewConversation = async (topic: TopicType): Promise<string> => {
    return await createNewSession(topic);
  };

  const handleSetCurrentSession = (session: SessionType | null) => {
    setCurrentSession(session);
    onSessionChange?.(session);
  };

  return {
    sessions,
    currentSession,
    fetchingSessions,
    createNewSession,
    deleteSession,
    updateSessionTitle,
    selectSession,
    searchSessions,
    loadSession,
    setCurrentSession: handleSetCurrentSession,
    createNewConversation
  };
};
